import {
  applyMiddleware,
  combineReducers,
  createStore
} from "redux";
import thunk from "redux-thunk";
import * as Documents from "./modules/read-only-documents";
import * as ReactComponents from "./modules/react-components";
import * as ComponentsMeta from "./modules/components-meta";
import * as PropControllers from "./modules/prop-controllers";
import * as PropControllerHandles from "./modules/prop-controller-handles";
import * as IsInBuilder from "./modules/is-in-builder";
import * as IsPreview from "./modules/is-preview";
import * as BuilderEditMode from "./modules/builder-edit-mode";
import * as Breakpoints from "./modules/breakpoints";
import * as Introspection from "../prop-controllers/introspection";
import { copyElementReference } from "../prop-controllers/copy";
import {
  copy as copyFromControl,
  getTranslatableData,
  merge,
  mergeTranslatedData
} from "../controls/control";
import {
  createDocument,
  createDocumentReference,
  isElementReference
} from "./modules/read-only-documents";
const reducer = combineReducers({
  documents: Documents.reducer,
  reactComponents: ReactComponents.reducer,
  componentsMeta: ComponentsMeta.reducer,
  propControllers: PropControllers.reducer,
  propControllerHandles: PropControllerHandles.reducer,
  isInBuilder: IsInBuilder.reducer,
  isPreview: IsPreview.reducer,
  builderEditMode: BuilderEditMode.reducer,
  breakpoints: Breakpoints.reducer
});
function getDocumentsStateSlice(state) {
  return state.documents;
}
function getDocument(state, documentKey) {
  return Documents.getDocument(getDocumentsStateSlice(state), documentKey);
}
function getReactComponentsStateSlice(state) {
  return state.reactComponents;
}
function getReactComponent(state, type) {
  return ReactComponents.getReactComponent(getReactComponentsStateSlice(state), type);
}
function getPropControllersStateSlice(state) {
  return state.propControllers;
}
function getPropControllerDescriptors(state) {
  return PropControllers.getPropControllerDescriptors(getPropControllersStateSlice(state));
}
function getComponentPropControllerDescriptors(state, componentType) {
  return PropControllers.getComponentPropControllerDescriptors(
    getPropControllersStateSlice(state),
    componentType
  );
}
function getPropControllerHandlesStateSlice(state) {
  return state.propControllerHandles;
}
function getPropControllers(state, documentKey, elementKey) {
  return PropControllerHandles.getPropControllers(
    getPropControllerHandlesStateSlice(state),
    documentKey,
    elementKey
  );
}
function normalizeElement(element, descriptors) {
  const elements = /* @__PURE__ */ new Map();
  const remaining = [element];
  let current;
  while (current = remaining.pop()) {
    elements.set(current.key, current);
    if (Documents.isElementReference(current))
      continue;
    const elementDescriptors = descriptors.get(current.type);
    if (elementDescriptors == null)
      continue;
    const parent = current;
    const children = Object.entries(elementDescriptors).reduce((acc, [propName, descriptor]) => {
      return [...acc, ...Introspection.getElementChildren(descriptor, parent.props[propName])];
    }, []);
    remaining.push(...children);
  }
  return elements;
}
function getDocumentElements(state, documentKey) {
  const document = getDocument(state, documentKey);
  const descriptors = getPropControllerDescriptors(state);
  if (document == null)
    return /* @__PURE__ */ new Map();
  return normalizeElement(document.rootElement, descriptors);
}
function getDocumentKeysSortedByDepth(state) {
  const documents = Documents.getDocuments(getDocumentsStateSlice(state));
  const keys = Array.from(documents.keys());
  if (keys.length < 2)
    return keys;
  const elements = /* @__PURE__ */ new Map();
  keys.forEach((key) => {
    elements.set(key, getDocumentElements(state, key));
  });
  keys.sort((a, b) => elements.get(a)?.has(b) ? -1 : 1);
  return keys;
}
function getElement(state, documentKey, elementKey) {
  return getDocumentElements(state, documentKey).get(elementKey) ?? null;
}
function getElementPropControllerDescriptors(state, documentKey, elementKey) {
  const element = getElement(state, documentKey, elementKey);
  if (element == null || Documents.isElementReference(element))
    return null;
  return getComponentPropControllerDescriptors(state, element.type);
}
function getElementId(state, documentKey, elementKey) {
  const element = getElement(state, documentKey, elementKey);
  if (element == null || Documents.isElementReference(element))
    return null;
  const descriptors = getComponentPropControllerDescriptors(state, element.type);
  if (descriptors == null)
    return null;
  const elementId = Object.entries(descriptors).reduce((acc, [propName, descriptor]) => {
    if (acc != null)
      return acc;
    return Introspection.getElementId(descriptor, element.props[propName]);
  }, null);
  return elementId;
}
function createReplacementContext(serializableReplacementContext) {
  const rc = {
    elementHtmlIds: new Set(serializableReplacementContext.elementHtmlIds),
    elementKeys: new Map(
      serializableReplacementContext.elementKeys && Object.entries(serializableReplacementContext.elementKeys)
    ),
    swatchIds: new Map(
      serializableReplacementContext.swatchIds && Object.entries(serializableReplacementContext.swatchIds)
    ),
    fileIds: new Map(
      serializableReplacementContext.fileIds && Object.entries(serializableReplacementContext.fileIds)
    ),
    typographyIds: new Map(
      serializableReplacementContext.typographyIds && Object.entries(serializableReplacementContext.typographyIds)
    ),
    tableIds: new Map(
      serializableReplacementContext.tableIds && Object.entries(serializableReplacementContext.tableIds)
    ),
    tableColumnIds: new Map(
      serializableReplacementContext.tableColumnIds && Object.entries(serializableReplacementContext.tableColumnIds)
    ),
    pageIds: new Map(
      serializableReplacementContext.pageIds && Object.entries(serializableReplacementContext.pageIds)
    ),
    globalElementIds: new Map(
      serializableReplacementContext.globalElementIds && Object.entries(serializableReplacementContext.globalElementIds)
    ),
    globalElementData: new Map(
      serializableReplacementContext.globalElementData && Object.entries(serializableReplacementContext.globalElementData)
    )
  };
  return rc;
}
function copyElementTree(state, elementTree, replacementContext) {
  function copyElementTreeNode(state2, replacementContext2) {
    return function(node) {
      const context = {
        replacementContext: replacementContext2,
        copyElement: copyElementTreeNode(state2, replacementContext2)
      };
      if (Documents.isElementReference(node)) {
        return { ...node, value: copyElementReference(node.value, context) };
      }
      const descriptors = getComponentPropControllerDescriptors(state2, node.type);
      if (descriptors == null)
        return node;
      for (const [propKey, descriptor] of Object.entries(descriptors)) {
        node.props[propKey] = copyFromControl(descriptor, node.props[propKey], context);
      }
      return node;
    };
  }
  const copy = JSON.parse(JSON.stringify(elementTree));
  return copyElementTreeNode(state, createReplacementContext(replacementContext))(copy);
}
function* traverseElementTree(state, elementTree) {
  yield elementTree;
  if (Documents.isElementReference(elementTree))
    return;
  const descriptors = getComponentPropControllerDescriptors(state, elementTree.type);
  if (descriptors == null)
    return;
  for (const [propKey, descriptor] of Object.entries(descriptors)) {
    const children = Introspection.getElementChildren(descriptor, elementTree.props[propKey]);
    for (const child of children) {
      if (!Documents.isElementReference(child))
        yield* traverseElementTree(state, child);
      yield child;
    }
  }
}
function getElementTreeTranslatableData(state, elementTree) {
  const translatableData = {};
  for (const element of traverseElementTree(state, elementTree)) {
    if (Documents.isElementReference(element))
      continue;
    const descriptors = getComponentPropControllerDescriptors(state, element.type);
    if (descriptors == null)
      continue;
    Object.entries(descriptors).forEach(([propName, descriptor]) => {
      const translatablePropData = getTranslatableData(descriptor, element.props[propName]);
      if (translatablePropData != null) {
        translatableData[`${element.key}:${propName}`] = translatablePropData;
      }
    });
  }
  return translatableData;
}
function mergeElementTreeTranslatedData(state, elementTree, translatedData) {
  function merge2(state2, translatedData2) {
    return function(node) {
      if (Documents.isElementReference(node))
        return node;
      const elementDescriptors = getPropControllerDescriptors(state2);
      const descriptors = elementDescriptors.get(node.type);
      if (descriptors == null) {
        throw new Error(`Can't merge element of type "${node.type}" because it has no descriptors`);
      }
      const context = {
        translatedData: translatedData2,
        mergeTranslatedData: merge2(state2, translatedData2)
      };
      const props = {};
      for (const propName of Object.keys(descriptors)) {
        const descriptor = descriptors[propName];
        props[propName] = mergeTranslatedData(
          descriptor,
          node.props[propName],
          translatedData2[`${node.key}:${propName}`],
          context
        );
      }
      return { ...node, props };
    };
  }
  return merge2(state, translatedData)(elementTree);
}
function mergeElement(state, baseElement, overrideElement) {
  if (baseElement.type !== overrideElement.type || baseElement.key !== overrideElement.key) {
    throw new Error(`Can't merge elements of different types or keys`);
  }
  if (Documents.isElementReference(overrideElement))
    return overrideElement;
  if (Documents.isElementReference(baseElement))
    return baseElement;
  const elementDescriptors = getPropControllerDescriptors(state);
  const descriptors = elementDescriptors.get(baseElement.type);
  if (descriptors == null) {
    throw new Error(
      `Can't merge element of type "${baseElement.type}" because it has no descriptors`
    );
  }
  const mergedProps = {};
  for (const propName of Object.keys(descriptors)) {
    const descriptor = descriptors[propName];
    const context = {
      mergeElement(base, override) {
        return mergeElement(state, base, override);
      }
    };
    mergedProps[propName] = merge(
      descriptor,
      baseElement.props[propName],
      overrideElement.props[propName],
      context
    );
  }
  return { ...baseElement, props: mergedProps };
}
function getIsInBuilder(state) {
  return state.isInBuilder;
}
function getIsPreview(state) {
  return IsPreview.getIsPreview(state.isPreview);
}
function getBuilderEditMode(state) {
  return state.builderEditMode;
}
function getBreakpoints(state) {
  return state.breakpoints;
}
function configureStore({
  rootElements,
  preloadedState,
  breakpoints
} = {}) {
  return createStore(
    reducer,
    {
      ...preloadedState,
      documents: Documents.getInitialState({ rootElements }),
      breakpoints: Breakpoints.getInitialState(breakpoints ?? preloadedState?.breakpoints)
    },
    applyMiddleware(thunk)
  );
}
export {
  configureStore,
  copyElementTree,
  createDocument,
  createDocumentReference,
  createReplacementContext,
  getBreakpoints,
  getBuilderEditMode,
  getComponentPropControllerDescriptors,
  getDocument,
  getDocumentKeysSortedByDepth,
  getElement,
  getElementId,
  getElementPropControllerDescriptors,
  getElementTreeTranslatableData,
  getIsInBuilder,
  getIsPreview,
  getPropControllerDescriptors,
  getPropControllers,
  getReactComponent,
  isElementReference,
  mergeElement,
  mergeElementTreeTranslatedData
};
//# sourceMappingURL=react-page.js.map